import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Url, URLS } from '@wix/da-url';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import {
  BiContextProvidedValue,
  BiData,
  type BiEvent,
  type TierUnsubscribeBiEvent,
  type TierViewButtonBiEvent,
  type TierSubscribeBiEvent,
} from '@wix/da-bi/pkg/events';
import type { PapiDeviation } from '@wix/da-papi-types';
import DiscountLabel from '../../DiscountLabel';
import { subscribeToTier } from '../../Tier/redux/consumer/actions';
import {
  isConsumerReduxReady,
  isMatureFilterOverridden,
} from '../../Tier/redux/consumer/selectors';
import { useDeviationMatureFilter } from '../../utils/hooks/useDeviationMatureFilter';
import { DeviationResourceType } from '../../types/deviation';
import { useRestrictToLoggedIn } from '../../utils/hooks/useRestrictToLoggedIn';
import { formatPrice } from '../../DeviationViews/TierProductCardDumb/utils';

interface SubscribeButtonOptions {
  type?: 'subscribe' | 'subscribed' | 'disabled' | 'view';
  matureFilterDisabled?: boolean;
  referral_info?: ReferralType;
  href?: string;
  /** "Unsubscribe" should instead be View, leading to the subs page */
  showUnsubAsView?: boolean;
  /** should display the price per month? */
  withPrice?: boolean;
  withSaleIndicator?: boolean;
}

export function useSubscribeButton(
  tier?: PapiDeviation,
  {
    type,
    matureFilterDisabled,
    referral_info,
    showUnsubAsView,
    withPrice,
    withSaleIndicator,
    href: hrefProp,
  }: SubscribeButtonOptions = {}
) {
  const waitingAction = useRef<any>(undefined);
  const [loadRedux, setLoadRedux] = useState(false);
  const { t } = useTranslation();
  const { showBlocked } = useDeviationMatureFilter(tier);
  const reduxReady = useSelector(isConsumerReduxReady);
  const matureFilterOverridden = useSelector(state =>
    isMatureFilterOverridden(state, tier?.deviationId)
  );
  const dispatch = useDispatch();
  const { restrictToLoggedIn } = useRestrictToLoggedIn();
  const isMatureBlocked = showBlocked && !matureFilterDisabled && !matureFilterOverridden;

  useEffect(() => {
    if (reduxReady && waitingAction.current) {
      waitingAction.current();
      waitingAction.current = undefined;
    }
  }, [reduxReady]);

  useEffect(() => {
    if (isMatureBlocked) {
      // mature blocked shows disabled, so we cannot wait for a click
      // to load the redux stuff
      setLoadRedux(true);
    }
  }, [isMatureBlocked]);

  const variant = useMemo(() => {
    if (!tier) {
      return 'disabled';
    }

    if (type) {
      return type;
    }

    if (tier?.tier?.isUserSubscribed) {
      if (showUnsubAsView) {
        return 'view';
      }
      return 'subscribed';
    }
    if (isMatureBlocked || !tier?.tier?.canUserSubscribe) {
      return 'disabled';
    }
    return 'subscribe';
  }, [isMatureBlocked, showUnsubAsView, tier, type]);

  const subscribe = useCallback(
    (e: React.MouseEvent) => {
      if (!tier) {
        return;
      }

      e.stopPropagation();
      e.preventDefault();

      if (!reduxReady) {
        waitingAction.current = () => {
          dispatch(subscribeToTier(tier));
        };
        setLoadRedux(true);
        return;
      }
      dispatch(subscribeToTier(tier));
    },

    [dispatch, reduxReady, tier]
  );

  const { onClick, href, biData, text, disabled } = useMemo((): {
    onClick?: (e: React.MouseEvent) => void;
    href?: string;
    biData?: BiEvent;
    text?: React.ReactNode;
    disabled?: boolean;
    reduxReady?: boolean;
  } => {
    const discount =
      tier?.extended?.saleDetails?.subscription?.eligibleDiscount || tier?.tier?.discount;
    switch (variant) {
      case 'subscribe':
        return {
          onClick: restrictToLoggedIn(ReferralType.MEMBER_SUBSCRIBE, subscribe),
          href: hrefProp,
          biData: BiData<TierSubscribeBiEvent>({
            evid: 550,
            typeid: DeviationResourceType.TIER,
            itemid: tier!.deviationId,
            referral_info: referral_info ?? BiContextProvidedValue,
          }),
          text: (
            <>
              {withPrice &&
                tier?.tier?.dollarPrice &&
                t('deviation.tier.subscribeWithPrice', {
                  dollarPrice: formatPrice(tier?.tier?.dollarPrice),
                })}
              {!withPrice && t('deviation.tier.subscribe')}
              {withSaleIndicator && discount && (
                <DiscountLabel discount={discount} textVariant="sale" size="small" reversedTheme />
              )}
            </>
          ),
          reduxReady,
        };
      case 'view':
        return {
          href: Url.userSubscriptionsPath({
            username: tier!.author!.username,
            tierDeviationId: tier?.deviationId,
            tierContentType: 'deviations',
          }),
          biData: BiData<TierViewButtonBiEvent>({
            evid: 544,
            typeid: DeviationResourceType.TIER,
            itemid: tier!.deviationId,
          }),
          text: t('deviation.tier.viewSubscription'),
          reduxReady,
        };
      case 'subscribed':
        return {
          href: hrefProp,
          biData: BiData<TierUnsubscribeBiEvent>({
            evid: 551,
            typeid: DeviationResourceType.TIER,
            itemid: tier!.deviationId,
            referral_info:
              (typeof window !== 'undefined' && window.location.href === URLS.ORDERS
                ? ReferralType.BILLING_PAYMENTS
                : ReferralType.TIER_CARD) || ReferralType.TIER_CARD,
          }),
          text: t('deviation.tier.subscribed'),
          reduxReady,
        };
      case 'disabled':
        return {
          text: t('deviation.tier.noSubscribing'),
          disabled: true,
          reduxReady,
        };
      default:
        return {};
    }
  }, [
    hrefProp,
    referral_info,
    restrictToLoggedIn,
    subscribe,
    t,
    tier,
    variant,
    withPrice,
    withSaleIndicator,
    reduxReady,
  ]);

  return { loadRedux, variant, onClick, href, biData, text, disabled };
}
